import {DashboardCurrent as Dashboard} from "@devplaninc/codeup";
import {parseDashboardA2UIJSON} from "@/lib/dashboards/a2ui";

type DashboardPresentation = {
  title: string;
  icon?: string;
  description?: string;
};

type DashboardPresentationCacheEntry = {
  documentJSON?: string;
  explicitTitle?: string;
  presentation: DashboardPresentation;
};

const dashboardPresentationCache = new WeakMap<Dashboard, DashboardPresentationCacheEntry>();

export function dashboardId(dashboard: Dashboard) {
  return dashboard.head?.id ?? "";
}

export function dashboardTitle(dashboard: Dashboard) {
  return dashboardPresentation(dashboard).title;
}

export function dashboardPresentation(dashboard: Dashboard): DashboardPresentation {
  const details = dashboard.version?.details;
  const documentJSON = details?.document?.json;
  const cached = dashboardPresentationCache.get(dashboard);
  if (cached && cached.documentJSON === documentJSON && cached.explicitTitle === details?.title) return cached.presentation;
  const document = parseDashboardA2UIJSON(documentJSON);
  const presentation = {
    title: details?.title || document?.title || "Untitled dashboard",
    icon: document?.icon,
    description: document?.summary ?? undefined,
  };
  dashboardPresentationCache.set(dashboard, {documentJSON, explicitTitle: details?.title, presentation});
  return presentation;
}

export function dashboardIcon(dashboard: Dashboard) {
  return dashboardPresentation(dashboard).icon;
}

export function dashboardDescription(dashboard: Dashboard) {
  return dashboardPresentation(dashboard).description;
}

export function dashboardLastUpdated(dashboard: Dashboard) {
  const details = dashboard.version?.details;
  return details?.lastRefreshedAt ?? details?.generatedAt;
}

export function compareDashboardPriority(a: Dashboard, b: Dashboard) {
  const favoriteOrder = Number(Boolean(b.head?.favorite)) - Number(Boolean(a.head?.favorite));
  if (favoriteOrder !== 0) return favoriteOrder;
  const updatedOrder = (dashboardLastUpdated(b)?.getTime() ?? 0) - (dashboardLastUpdated(a)?.getTime() ?? 0);
  return updatedOrder || dashboardTitle(a).localeCompare(dashboardTitle(b)) || dashboardId(a).localeCompare(dashboardId(b));
}
